import { Component, OnInit, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Actions } from 'src/app/models';
import { Client } from 'src/app/models/client';
import { Department } from 'src/app/models/department';
import { ClientService, SecurityService, WorkflowContextService } from 'src/app/services';

@Component({
    selector: 'wm-inspection-app-licenses-list',
    templateUrl: './inspection-app-licenses-list.component.html',
    styleUrl: './inspection-app-licenses-list.component.css',
    standalone: false
})
export class InspectionAppLicensesListComponent implements OnInit, OnDestroy {
  clients: Client[] = [];
  departments: Department[] = [];
  clientsByState: Map<string, Client[]> = new Map();
  statesSorted: string[] = [];
  expandedClientId: string | null = null;
  canEditGlobal = false;
  loading = false;
  savingClientId: string | null = null;
  savingDepartmentId: string | null = null;

  constructor(
    private _securitySvc: SecurityService,
    private _clientSvc: ClientService,
    private _toastr: ToastrService
  ) {}

  ngOnInit() {
    this._securitySvc
      .isLoginEntitled(Actions.DO_ANYTHING)
      .subscribe(r => {
        this.canEditGlobal = r;
      });

    this.loadClientsAndDepartments();
  }

  ngOnDestroy() {
  }

  loadClientsAndDepartments() {
    this.loading = true;
    this._clientSvc.getClients().subscribe(
      clients => {
        this.clients = clients || [];
        if (this.clients.length > 0) {
          this.loadDepartmentsForAllClients();
        } else {
          this.loading = false;
        }
      },
      error => {
        this._toastr.error('Failed to load clients');
        this.loading = false;
      }
    );
  }

  loadDepartmentsForAllClients() {
    let loadedCount = 0;
    this.departments = [];

    this.clients.forEach(client => {
      this._clientSvc.getClientDepartments(client.id).subscribe(
        departments => {
          this.departments = [...this.departments, ...(departments || [])];
          loadedCount++;
          if (loadedCount === this.clients.length) {
            this.groupClientsByState();
            this.loading = false;
          }
        },
        error => {
          this._toastr.error(`Failed to load departments for client "${client.name}"`);
          loadedCount++;
          if (loadedCount === this.clients.length) {
            this.groupClientsByState();
            this.loading = false;
          }
        }
      );
    });
  }

  groupClientsByState() {
    this.clientsByState.clear();

    // Group clients by state
    this.clients.forEach(client => {
      const state = client.state || 'Unknown';
      if (!this.clientsByState.has(state)) {
        this.clientsByState.set(state, []);
      }
      this.clientsByState.get(state)!.push(client);
    });

    // Sort states alphabetically
    this.statesSorted = Array.from(this.clientsByState.keys()).sort();
  }

  getDepartmentsForClient(clientId: string): Department[] {
    return this.departments.filter(d => d.clientId === clientId);
  }

  toggleClientExpanded(clientId: string) {
    this.expandedClientId = this.expandedClientId === clientId ? null : clientId;
  }

  isClientExpanded(clientId: string): boolean {
    return this.expandedClientId === clientId;
  }

  toggleClientLicense(client: Client) {
    if (!this.canEditGlobal) {
      this._toastr.warning('You do not have permission to edit inspection app licenses');
      return;
    }

    this.savingClientId = client.id;
    const originalValue = client.hasNonDepartmentalInspectionAppLicense;
    client.hasNonDepartmentalInspectionAppLicense = !client.hasNonDepartmentalInspectionAppLicense;
    
    this._clientSvc.saveClientNonDepartmentalInspectionAppLicense(client.id, client.hasNonDepartmentalInspectionAppLicense).subscribe(
      result => {
        this._toastr.success(`Client "${client.name}" updated successfully`);
        this.savingClientId = null;
      },
      error => {
        client.hasNonDepartmentalInspectionAppLicense = originalValue;
        this._toastr.error('Failed to update client');
        this.savingClientId = null;
      }
    );
  }

  toggleDepartmentLicense(department: Department) {
    if (!this.canEditGlobal) {
      this._toastr.warning('You do not have permission to edit inspection app licenses');
      return;
    }

    this.savingDepartmentId = department.id;
    const originalValue = department.hasInspectionAppLicense;
    department.hasInspectionAppLicense = !department.hasInspectionAppLicense;

    this._clientSvc.saveDepartmentInspectionAppLicense(department.id, department.hasInspectionAppLicense).subscribe(
      result => {
        this._toastr.success(`Department "${department.name}" updated successfully`);
        this.savingDepartmentId = null;
      },
      error => {
        department.hasInspectionAppLicense = originalValue;
        this._toastr.error('Failed to update department');
        this.savingDepartmentId = null;
      }
    );
  }
}
